import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HealthIndicator } from '../../_shared/components/HealthIndicator';
import { formatGatewayDate, normalizeHealthState } from '../../_shared/utils/format';
import type { TargetRow } from '../types/target';

interface TargetDetailDialogProps {
  target: TargetRow;
  onClose: () => void;
}

/** 타겟 상세 read-only 다이얼로그 (#621) — lastError 전문 및 헬스 카운터를 표시한다. */
export function TargetDetailDialog({ target, onClose }: TargetDetailDialogProps) {
  const { t } = useTranslation();
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    const id = requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      document.removeEventListener('keydown', handler);
      cancelAnimationFrame(id);
    };
  }, [onClose]);

  return (
    <div
      role="presentation"
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('gateway.targets.detail.title', { host: target.host, port: target.port })}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-surface-background)', borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-6)', minWidth: '480px', maxWidth: '640px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontFamily: 'var(--font-family-mono)' }}>
            {target.host}:{target.port}
          </h2>
          <button ref={closeBtnRef} type="button" onClick={onClose} style={closeBtnStyle} aria-label={t('gateway.shared.actions.close')}>
            &times;
          </button>
        </div>

        <dl style={dlStyle}>
          <dt style={dtStyle}>{t('gateway.targets.columns.service')}</dt>
          <dd style={ddMonoStyle}>{target.serviceId}</dd>

          <dt style={dtStyle}>{t('gateway.targets.columns.weight')}</dt>
          <dd style={ddStyle}>{target.weight}</dd>

          <dt style={dtStyle}>{t('gateway.targets.columns.source')}</dt>
          <dd style={ddStyle}>{target.source}</dd>

          <dt style={dtStyle}>{t('gateway.targets.columns.health')}</dt>
          <dd style={ddStyle}>
            <HealthIndicator state={normalizeHealthState(target.healthStatus)} showLabel size="sm" latencyMs={target.lastLatencyMs ?? undefined} />
          </dd>

          <dt style={dtStyle}>{t('gateway.targets.detail.lastCheckedAt')}</dt>
          <dd style={ddStyle}>{target.lastCheckedAt ? formatGatewayDate(target.lastCheckedAt) : '-'}</dd>

          <dt style={dtStyle}>{t('gateway.targets.detail.consecutiveFailures')}</dt>
          <dd style={ddStyle}>{target.consecutiveFailures}</dd>

          <dt style={dtStyle}>{t('gateway.targets.detail.consecutiveSuccesses')}</dt>
          <dd style={ddStyle}>{target.consecutiveSuccesses}</dd>

          <dt style={dtStyle}>{t('gateway.targets.detail.lastError')}</dt>
          <dd style={{ ...ddStyle, wordBreak: 'break-word' }}>{target.lastError || '-'}</dd>
        </dl>
      </div>
    </div>
  );
}

const dlStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '160px 1fr',
  rowGap: 'var(--spacing-2)',
  columnGap: 'var(--spacing-3)',
  margin: 'var(--spacing-4) 0 0',
};

const dtStyle: React.CSSProperties = { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' };
const ddStyle: React.CSSProperties = { margin: 0, fontSize: 'var(--font-size-sm)' };
const ddMonoStyle: React.CSSProperties = { ...ddStyle, fontFamily: 'var(--font-family-mono)' };

const closeBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: 'var(--font-size-xl, 24px)',
  lineHeight: 1,
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
};
